/*
 * PublicCatalogPage.jsx
 *
 * Public, no-login catalog of all CrisAngel services + all payment methods.
 * Accessible via /servicios (short shareable link). Designed for clients who
 * discover CrisAngel through WhatsApp / TikTok and want to explore the
 * offering before registering.
 *
 * Sections:
 *   1. Hero + intro
 *   2. Service categories & cards (image + price MXN + USD + badge)
 *   3. Service-detail modal (features, description, 4-step process,
 *      WhatsApp CTA, "regístrate" CTA)
 *   4. Payment methods (4 tabs with tutorials + copy-to-clipboard)
 *   5. Registration CTA
 *   6. Footer with contact
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Wand2, Flame, Moon, Star, Crown, Shield, Zap,
  Check, Copy, MessageCircle, Phone, ArrowRight, ArrowLeft, X,
  Banknote, CreditCard, Store, Send, MapPin, Info, ChevronRight,
  UserPlus, Globe2, PhoneCall, Clock, Heart, Skull, Sparkle,
  Home, MessageSquareQuote, UserCircle2, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { BANK_INFO } from "@/data/services";
import { TOTAL_COMMENTS } from "@/data/commentsGenerator";
import { useCatalog, FIXED_CATEGORIES } from "@/hooks/useCatalog";
import { useAuth } from "@/contexts/AuthContext";
import { PriceLock, hasPrice, fmtMXN, fmtUSD } from "@/components/PriceLock";

const useAccountLink = () => {
  const { user } = useAuth();
  return user
    ? { to: "/mi-cuenta", label: "Ir a mi perfil", short: "Mi perfil", Icon: UserCircle2, loggedIn: true }
    : { to: "/registro?next=/servicios", label: "Registrarme gratis", short: "Registrarme", Icon: UserPlus, loggedIn: false };
};

// --------------------------------------------------------------------------
// Per-service visuals (icon + gradient) — no external images so the page
// loads instantly and never breaks with CORS / hotlink issues.
// --------------------------------------------------------------------------
const SERVICE_VISUALS = {
  lectura_basica:       { Icon: Star,    gradient: "from-sky-500/40 via-indigo-500/30 to-purple-500/20", accent: "text-sky-300" },
  lectura_completa:     { Icon: Sparkle, gradient: "from-fuchsia-500/40 via-purple-500/30 to-indigo-500/20", accent: "text-fuchsia-300" },
  lectura_completa_video: { Icon: Wand2, gradient: "from-pink-500/40 via-fuchsia-500/30 to-purple-500/30", accent: "text-pink-300" },
  magia_blanca:      { Icon: Moon,    gradient: "from-slate-100/20 via-white/10 to-amber-200/20", accent: "text-amber-100" },
  magia_negra:       { Icon: Shield,  gradient: "from-red-900/40 via-black to-purple-900/40", accent: "text-red-300" },
  magia_suprema:     { Icon: Crown,   gradient: "from-amber-600/40 via-yellow-500/30 to-orange-600/30", accent: "text-amber-300" },
  poder_maximo:      { Icon: Zap,     gradient: "from-orange-600/50 via-red-600/40 to-amber-500/40", accent: "text-orange-300" },
  pacto_luz:         { Icon: Sparkles,gradient: "from-cyan-400/30 via-blue-400/30 to-white/20", accent: "text-cyan-200" },
  pacto_satanico:    { Icon: Flame,   gradient: "from-red-700/50 via-orange-700/40 to-black", accent: "text-red-400" },
};

// Category labels
const CATEGORY_LABEL = {
  lectura: {
    label: "Lecturas Espirituales",
    desc: "Consultas por llamada o videollamada con tarot, runas y baraja española.",
    cta: "Me Interesa una Lectura",
    tagline: "Consultas por llamada o videollamada",
    Icon: Star,
    gradient: "from-sky-500/40 via-indigo-500/30 to-purple-500/20",
    accent: "text-sky-300",
    ring: "ring-sky-500/40",
    perks: [
      "Respuestas claras a lo que te preocupa",
      "Tarot, runas y baraja española",
      "Se agenda desde el mismo día",
    ],
  },
  hechiceria: {
    label: "Hechicería · Trabajos Espirituales",
    desc: "Trabajos de magia con resultados reales, protección y efecto duradero.",
    cta: "Me Interesa un Trabajo de Hechicería",
    tagline: "Trabajos de magia con resultados reales",
    Icon: Flame,
    gradient: "from-amber-500/40 via-red-500/30 to-purple-700/30",
    accent: "text-amber-300",
    ring: "ring-amber-500/40",
    perks: [
      "Amarres, protección, prosperidad, revanchas",
      "Rituales personalizados con velas, hierbas y ofrendas",
      "Lectura incluida sin costo",
    ],
  },
  pacto: {
    label: "Pactos Espirituales",
    desc: "El nivel más alto: pactos permanentes con resultados casi inmediatos.",
    cta: "Me Interesa un Pacto",
    tagline: "Pactos permanentes de máximo poder",
    Icon: Crown,
    gradient: "from-cyan-500/40 via-purple-500/40 to-red-700/40",
    accent: "text-cyan-200",
    ring: "ring-cyan-500/40",
    perks: [
      "Resultados casi inmediatos y permanentes",
      "Compromiso espiritual del más alto nivel",
      "Lectura y seguimiento personalizado",
    ],
  },
};

// Meta for categories created from the admin panel / AI assistant
const extraCategoryMeta = (cat) => ({
  label: cat.label?.es || cat.name || cat.id,
  desc: "Servicios adicionales del catálogo.",
  cta: `Me Interesa: ${cat.label?.es || cat.name || cat.id}`,
  tagline: "Servicios del catálogo",
  Icon: Sparkles,
  gradient: "from-purple-500/40 via-fuchsia-500/30 to-indigo-500/20",
  accent: "text-purple-200",
  ring: "ring-purple-500/40",
  perks: ["Atención personalizada", "Confirmación por WhatsApp", "Métodos de pago flexibles"],
});

// --------------------------------------------------------------------------
// Payment methods (all 4)
// --------------------------------------------------------------------------
const CARD = BANK_INFO.card;
const CLABE = BANK_INFO.clabe;
const BENEFICIARY = "CrisAngel Astral Rodriguez Jaime";
const PHONE = "+52 662 287 0805";
const PHONE_CLEAN = "526622870805";
const WHATSAPP_LINK = `https://wa.me/${PHONE_CLEAN}`;
const CITY = "Guaymas";
const STATE = "Sonora";
const COUNTRY = "México";

const PAYMENT_METHODS = [
  {
    id: "remittance-bank",
    Icon: Send,
    title: "Envío de dinero a cuenta bancaria",
    subtitle: "Remitly · Xoom · Wise",
    platforms: [
      { name: "Remitly", tag: "app / web", color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" },
      { name: "Xoom (PayPal)", tag: "app / web", color: "bg-blue-500/10 border-blue-500/30 text-blue-300" },
      { name: "Wise", tag: "app / web", color: "bg-lime-500/10 border-lime-500/30 text-lime-300" },
    ],
    intro: "Ideal si tienes cuenta o tarjeta en cualquier país y quieres enviar el dinero directamente a mi cuenta bancaria en México.",
    dataBlock: "bank_full",  // full bank data
    tutorial: {
      title: "¿Es tu primera vez enviando dinero? Sigue estos pasos",
      steps: [
        "Descarga la app oficial: Remitly, Xoom o Wise desde App Store o Google Play (todas gratis). Regístrate con tu correo y tu ID.",
        "Elige enviar dinero a MÉXICO en pesos (MXN). Selecciona la opción \"Depósito a cuenta bancaria\" o \"Bank deposit\".",
        "Elige BBVA México y captura la CLABE 012770011161566570 o la tarjeta 4152 3146 4391 4446 (cualquiera de las dos funciona).",
        "Como Beneficiario escribe: CrisAngel Astral Rodriguez Jaime. El teléfono opcional es +52 662 287 0805.",
        "Paga con tu tarjeta o cuenta bancaria de tu país. Envíame el comprobante por WhatsApp y confirmamos la recepción.",
      ],
    },
  },
  {
    id: "remittance-pickup",
    Icon: MapPin,
    title: "Envío para retiro en ventanilla",
    subtitle: "Western Union · MoneyGram · Ria · Wells Fargo",
    platforms: [
      { name: "Western Union", tag: "ventanilla", color: "bg-yellow-500/10 border-yellow-500/40 text-yellow-300" },
      { name: "MoneyGram",     tag: "ventanilla", color: "bg-red-500/10 border-red-500/40 text-red-300" },
      { name: "Ria Money Transfer", tag: "ventanilla", color: "bg-orange-500/10 border-orange-500/40 text-orange-300" },
      { name: "Wells Fargo Express Send", tag: "ventanilla", color: "bg-red-700/10 border-red-700/40 text-red-200" },
    ],
    intro: "El dinero se retira en persona en México. Yo recojo con mi INE en cualquiera de estas cadenas:",
    pickupChains: [
      { name: "Banco Azteca", note: "cualquier sucursal" },
      { name: "Soriana",      note: "cualquier tienda" },
      { name: "BBVA",         note: "sucursal / ventanilla" },
    ],
    dataBlock: "pickup",  // beneficiary + phone + city/state
    tutorial: {
      title: "Cómo enviar dinero para retiro en ventanilla",
      steps: [
        "Descarga la app (Western Union, MoneyGram, Ria o Wells Fargo) desde App Store / Google Play, o acude a una sucursal en tu país.",
        "Elige enviar a MÉXICO y selecciona \"Retiro en efectivo\" o \"Cash pickup\".",
        "Como cadena de retiro elige Banco Azteca, Soriana o BBVA (según la app; si no aparece, usa Ria o WU que aceptan cualquiera).",
        "Beneficiario: CrisAngel Astral Rodriguez Jaime · Teléfono: +52 662 287 0805 · Ciudad: Guaymas · Estado: Sonora · País: México.",
        "Al terminar, la app te dará un CÓDIGO / MTCN / referencia. Envíame ese código por WhatsApp para retirarlo el mismo día.",
      ],
    },
  },
  {
    id: "convenience-store",
    Icon: Store,
    title: "Pago en tienda de conveniencia",
    subtitle: "Oxxo · Farmacia Guadalajara · 7-Eleven · Circle K · Walmart · Etc.",
    platforms: [
      { name: "Oxxo",                tag: "tienda", color: "bg-red-500/10 border-red-500/40 text-red-300" },
      { name: "Farmacia Guadalajara", tag: "tienda", color: "bg-yellow-500/10 border-yellow-500/40 text-yellow-300" },
      { name: "7-Eleven",            tag: "tienda", color: "bg-green-500/10 border-green-500/40 text-green-300" },
      { name: "Circle K",            tag: "tienda", color: "bg-orange-500/10 border-orange-500/40 text-orange-300" },
      { name: "Walmart / Bodega",    tag: "tienda", color: "bg-blue-500/10 border-blue-500/40 text-blue-300" },
    ],
    intro: "Perfecto si estás en México y no tienes cuenta bancaria. Pagas EN EFECTIVO con solo mostrar el número de tarjeta en la caja.",
    dataBlock: "bank_full",
    tutorial: {
      title: "Cómo depositar en Oxxo / tienda de conveniencia",
      steps: [
        "Acude a cualquier Oxxo, Farmacia Guadalajara, 7-Eleven o Circle K con el dinero en efectivo.",
        "En caja di: \"Quiero hacer un DEPÓSITO a tarjeta BBVA\" y muestra el número: 4152 3146 4391 4446.",
        "El cajero te pedirá el monto exacto y una comisión pequeña (~$11 pesos). Te da un ticket con la referencia.",
        "Envíame la foto del ticket por WhatsApp para confirmar tu abono. En 24-48 h se ve reflejado.",
      ],
    },
  },
  {
    id: "bank-transfer",
    Icon: Banknote,
    title: "Transferencia bancaria (SPEI)",
    subtitle: "BBVA · HSBC · Banco Azteca · Banorte · Santander · Citibanamex · Etc.",
    platforms: [
      { name: "BBVA",         tag: "banco MX", color: "bg-blue-600/10 border-blue-600/40 text-blue-300" },
      { name: "HSBC",         tag: "banco MX", color: "bg-red-600/10 border-red-600/40 text-red-300" },
      { name: "Banco Azteca", tag: "banco MX", color: "bg-emerald-600/10 border-emerald-600/40 text-emerald-300" },
      { name: "Banorte",      tag: "banco MX", color: "bg-red-500/10 border-red-500/40 text-red-300" },
      { name: "Santander",    tag: "banco MX", color: "bg-red-700/10 border-red-700/40 text-red-200" },
      { name: "Citibanamex",  tag: "banco MX", color: "bg-blue-500/10 border-blue-500/40 text-blue-300" },
      { name: "Scotiabank",   tag: "banco MX", color: "bg-red-400/10 border-red-400/40 text-red-200" },
      { name: "Inbursa",      tag: "banco MX", color: "bg-yellow-400/10 border-yellow-400/40 text-yellow-300" },
    ],
    intro: "El más rápido si ya tienes app bancaria mexicana. La transferencia llega a mi cuenta BBVA en segundos (SPEI).",
    dataBlock: "bank_full",
    tutorial: {
      title: "Cómo transferir por SPEI desde cualquier app bancaria mexicana",
      steps: [
        "Abre la app de tu banco (BBVA, HSBC, Banorte, Santander, Azteca, etc.) e ingresa a \"Transferir\" o \"Enviar dinero\".",
        "Selecciona \"A otro banco (SPEI)\" o \"Transferencia interbancaria\".",
        "Da de alta la cuenta con la CLABE: 012770011161566570 · Banco: BBVA México · Beneficiario: CrisAngel Astral Rodriguez Jaime.",
        "Escribe el monto y en concepto pon tu nombre y el servicio (ej. \"Juan López - Magia Blanca\").",
        "Confirma con tu Token / NIP. En segundos me llega. Envíame el comprobante por WhatsApp.",
      ],
    },
  },
];

// --------------------------------------------------------------------------
// COMPONENTS
// --------------------------------------------------------------------------
const CopyableField = ({ testId, label, value, big = false }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No pudimos copiar");
    }
  };
  return (
    <button
      onClick={doCopy}
      data-testid={testId}
      className="w-full text-left rounded-xl border border-[#d4af37]/25 bg-black/40 hover:border-[#d4af37]/60 hover:bg-[#d4af37]/5 transition-all px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-3 group"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.25em] sm:tracking-[0.3em] text-neutral-500 mb-0.5">{label}</div>
        <div className={`text-white font-mono ${big ? "text-base sm:text-xl" : "text-xs sm:text-sm"} break-all leading-tight`}>{value}</div>
      </div>
      <div className={`shrink-0 text-[9px] sm:text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${copied ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-neutral-400 group-hover:text-[#d4af37] group-hover:border-[#d4af37]/40"}`}>
        {copied ? <><Check className="w-3 h-3 inline mr-0.5" /> Copiado</> : <><Copy className="w-3 h-3 inline mr-0.5" /> Copiar</>}
      </div>
    </button>
  );
};

const ServiceCard = ({ svc, onOpen }) => {
  const viz = SERVICE_VISUALS[svc.id] || { Icon: Sparkles, gradient: "from-purple-500/30 to-black", accent: "text-purple-300" };
  const { Icon } = viz;
  return (
    <button
      data-testid={`svc-card-${svc.id}`}
      onClick={() => onOpen(svc)}
      className="group relative text-left rounded-2xl overflow-hidden border border-white/10 bg-black/40 hover:border-[#d4af37]/60 hover:shadow-[0_0_35px_rgba(212,175,55,0.15)] transition-all active:scale-[0.99]"
    >
      {/* Visual header */}
      <div className={`relative h-32 sm:h-40 bg-gradient-to-br ${viz.gradient} overflow-hidden`}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Icon className={`w-20 h-20 sm:w-24 sm:h-24 ${viz.accent} opacity-40 group-hover:scale-110 transition-transform duration-500`} />
        </div>
        {svc.badge && (
          <div className="absolute top-2.5 left-2.5 text-[9px] sm:text-[10px] uppercase tracking-widest bg-[#d4af37] text-black px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-bold shadow-lg">
            {svc.badge.es}
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5">
        <h3 className={`font-serif text-lg sm:text-xl ${viz.accent} leading-tight mb-1`}>{svc.name.es}</h3>
        <p className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-widest mb-2 sm:mb-3">{svc.subtitle.es}</p>
        <p className="text-[13px] sm:text-sm text-neutral-300 leading-relaxed line-clamp-3 mb-3 sm:mb-4">{svc.description.es}</p>

        <div className="flex items-end justify-between gap-2 mb-2 sm:mb-3">
          <div className="min-w-0">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-500">Precio</div>
            {hasPrice(svc.priceMXN) ? (
              <>
                <div className="text-white font-serif text-lg sm:text-xl leading-tight" data-testid={`svc-card-price-${svc.id}`}>{fmtMXN(svc.priceMXN)} <span className="text-[11px] sm:text-xs text-neutral-400">MXN</span></div>
                <div className="text-[10px] sm:text-[11px] text-neutral-400">≈ USD {fmtUSD(svc.priceUSD)}</div>
              </>
            ) : (
              <div className="mt-1"><PriceLock size="sm" testId={`svc-card-lock-${svc.id}`} /></div>
            )}
          </div>
          <div className="flex items-center gap-1 text-[9px] sm:text-[10px] uppercase tracking-widest text-[#d4af37] group-hover:translate-x-1 transition-transform shrink-0">
            Ver detalle <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </div>
        </div>

        <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
          <Clock className="w-3 h-3" /> {svc.duration.es}
        </div>
      </div>
    </button>
  );
};

const REQUEST_STEPS = [
  {
    icon: UserPlus,
    title: "1. Regístrate en la página",
    body: "Crea tu cuenta gratis con tu nombre, WhatsApp y correo. Es rápido (1 minuto) y te permite ver precios, recibir tu comprobante, tus notificaciones y agendar tu cita.",
    cta: { label: "Registrarme ahora", to: "/registro?next=/servicios" },
    loggedIn: {
      title: "1. Ya tienes tu cuenta",
      body: "Con tu cuenta recibes tu comprobante, tus notificaciones y puedes agendar tu cita. Revisa tus datos y tu progreso en Mi perfil.",
      cta: { label: "Ir a mi perfil", to: "/mi-cuenta" },
    },
  },
  {
    icon: MessageCircle,
    title: "2. Envíame WhatsApp",
    body: "Escríbeme para confirmar el servicio que elegiste. Yo te confirmo disponibilidad, tiempos exactos y el método de pago que más te convenga.",
  },
  {
    icon: Banknote,
    title: "3. Realiza tu pago",
    body: "Elige entre transferencia SPEI, Oxxo/tienda, envío internacional o retiro en ventanilla (Western Union, Ria, MoneyGram, Wells Fargo).",
  },
];

// The 4th step differs by service. Kept as a function so the copy can
// evolve per service without touching the main render pipeline.
const getStep4 = (svc) => {
  if (svc?.id === "lectura_basica") {
    return {
      icon: Sparkles,
      title: "4. Se agenda tu cita",
      body: "Se agenda la cita para atenderse desde el día siguiente en adelante.",
    };
  }
  if (svc?.id === "lectura_completa" || svc?.id === "lectura_completa_video") {
    return {
      icon: Sparkles,
      title: "4. Se agenda tu cita",
      body: "Se agenda la cita para atenderse desde el mismo día en adelante.",
    };
  }
  // Hechicería + pactos: seguimiento del trabajo + lectura incluida
  return {
    icon: Sparkles,
    title: "4. Inicia tu trabajo espiritual",
    body: "Se inicia el seguimiento de tu trabajo y se agenda tu lectura incluida.",
  };
};

const ServiceDetailModal = ({ svc, onClose }) => {
  const acct = useAccountLink();
  if (!svc) return null;
  const viz = SERVICE_VISUALS[svc.id] || { Icon: Sparkles, gradient: "from-purple-500/30 to-black", accent: "text-purple-300" };
  const { Icon } = viz;
  const waMsg = encodeURIComponent(
    `Hola Crisangel 🌙\nMe interesa el servicio "${svc.name.es}"${hasPrice(svc.priceMXN) ? ` (${fmtMXN(svc.priceMXN)} MXN / USD ${fmtUSD(svc.priceUSD)})` : ""}.\n\n¿Me puedes dar más información y confirmarme disponibilidad?`,
  );
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm px-2 sm:px-3 py-4 sm:py-10 overflow-y-auto"
      data-testid={`svc-modal-${svc.id}`}
      onClick={onClose}
    >
      <div
        className="max-w-2xl mx-auto rounded-2xl overflow-hidden border border-[#d4af37]/40 bg-[#08040d] shadow-[0_0_60px_rgba(212,175,55,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`relative h-32 sm:h-48 bg-gradient-to-br ${viz.gradient}`}>
          <button
            data-testid="svc-modal-close"
            onClick={onClose}
            className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-black/70 border border-white/10 text-white hover:bg-black/90 flex items-center justify-center z-10"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Icon className={`w-24 h-24 sm:w-28 sm:h-28 ${viz.accent} opacity-60`} />
          </div>
          {svc.badge && (
            <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 text-[9px] sm:text-[10px] uppercase tracking-widest bg-[#d4af37] text-black px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-bold">
              {svc.badge.es}
            </div>
          )}
        </div>

        <div className="p-4 sm:p-7 space-y-5 sm:space-y-6">
          <div>
            <h2 className={`font-serif text-xl sm:text-3xl ${viz.accent} leading-tight`}>{svc.name.es}</h2>
            <p className="text-[10px] sm:text-xs text-neutral-500 uppercase tracking-widest mt-1">{svc.subtitle.es}</p>
            <p className="mt-2 sm:mt-3 text-[13px] sm:text-sm text-neutral-200 leading-relaxed">{svc.description.es}</p>
          </div>

          {/* Price block */}
          <div className="rounded-2xl border border-[#d4af37]/40 bg-black/50 p-3 sm:p-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-500 mb-0.5">Precio del servicio</div>
              {hasPrice(svc.priceMXN) ? (
                <>
                  <div className="font-serif text-2xl sm:text-3xl text-white leading-tight" data-testid="svc-modal-price">{fmtMXN(svc.priceMXN)} <span className="text-xs sm:text-sm text-neutral-400">MXN</span></div>
                  <div className="text-xs sm:text-sm text-neutral-300">≈ USD {fmtUSD(svc.priceUSD)}</div>
                </>
              ) : (
                <div className="mt-1"><PriceLock testId="svc-modal-lock" /></div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-500">Duración</div>
              <div className="text-xs sm:text-sm text-white">{svc.duration.es}</div>
            </div>
          </div>

          {/* Features */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2 flex items-center gap-1">
              <Sparkle className="w-3 h-3" /> Qué incluye
            </div>
            <ul className="space-y-1.5">
              {svc.features.es.map((f, i) => (
                <li key={i} className="text-sm text-neutral-200 flex gap-2">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Steps to request */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-3 flex items-center gap-1">
              <Info className="w-3 h-3" /> Cómo solicitar este servicio · 4 pasos simples
            </div>
            <div className="space-y-2">
              {[...REQUEST_STEPS, getStep4(svc)].map((s0, i) => {
                const s = acct.loggedIn && s0.loggedIn ? { ...s0, ...s0.loggedIn } : s0;
                const SIcon = s.icon;
                return (
                  <div key={i} className="flex gap-3 items-start rounded-xl bg-black/40 border border-white/5 px-3 py-2.5">
                    <SIcon className="w-4 h-4 text-[#d4af37] shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-semibold">{s.title}</div>
                      <div className="text-xs text-neutral-400 leading-relaxed">{s.body}</div>
                      {s.cta && (
                        <Link
                          to={s.cta.to}
                          className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-[#d4af37] hover:text-[#f0c050] underline decoration-dotted"
                        >
                          {s.cta.label} <ChevronRight className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <a
              data-testid="svc-wa-cta"
              href={`${WHATSAPP_LINK}?text=${waMsg}`}
              target="_blank"
              rel="noreferrer"
              className="w-full py-3 rounded-full bg-emerald-500 text-black font-semibold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-400"
            >
              <MessageCircle className="w-4 h-4" /> Solicitar por WhatsApp
            </a>
            <Link
              data-testid={acct.loggedIn ? "svc-profile-cta" : "svc-register-cta"}
              to={acct.to}
              className="w-full py-3 rounded-full btn-gold font-semibold text-xs uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <acct.Icon className="w-4 h-4" /> {acct.label}
            </Link>
          </div>

          <p className="text-[11px] text-neutral-500 italic text-center">
            {acct.loggedIn
              ? "Después de pagar, sube tu comprobante en Mi perfil → Mis Pagos para que lo revise y agendemos tu cita."
              : "Regístrate gratis para ver precios, subir tu comprobante y agendar tu cita."}
          </p>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Payment method card
// --------------------------------------------------------------------------
const PaymentDataBlock = ({ variant }) => {
  if (variant === "bank_full") {
    return (
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] flex items-center gap-1">
          <Banknote className="w-3 h-3" /> Datos bancarios
        </div>
        <CopyableField testId="pay-bank"     label="Banco"                value="BBVA Bancomer México" />
        <CopyableField testId="pay-clabe"    label="CLABE Interbancaria"  value={CLABE} big />
        <CopyableField testId="pay-card"     label="Tarjeta"              value={CARD} big />
        <CopyableField testId="pay-benef"    label="Beneficiario"         value={BENEFICIARY} />
        <CopyableField testId="pay-whatsapp" label="WhatsApp y llamadas"  value={PHONE} />
        <div className="text-[11px] text-neutral-500 pt-1">Estado: {STATE} · País: {COUNTRY}</div>
      </div>
    );
  }
  if (variant === "pickup") {
    return (
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37] flex items-center gap-1">
          <MapPin className="w-3 h-3" /> Datos para retiro en ventanilla
        </div>
        <CopyableField testId="pay-pickup-benef" label="Beneficiario"    value={BENEFICIARY} />
        <CopyableField testId="pay-pickup-phone" label="Tel."            value={PHONE} />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
          <CopyableField testId="pay-pickup-city"    label="Ciudad" value={CITY} />
          <CopyableField testId="pay-pickup-state"   label="Estado" value={STATE} />
          <CopyableField testId="pay-pickup-country" label="País"   value={COUNTRY} />
        </div>
      </div>
    );
  }
  return null;
};

const PaymentMethodCard = ({ method }) => {
  const [open, setOpen] = useState(false);
  const { Icon } = method;
  return (
    <div
      data-testid={`pay-method-${method.id}`}
      className="rounded-2xl border border-[#d4af37]/25 bg-black/40 overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`pay-method-toggle-${method.id}`}
        className="w-full flex items-center gap-3 p-3.5 sm:p-5 text-left hover:bg-[#d4af37]/[0.04]"
      >
        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-white font-serif text-base sm:text-lg leading-tight">{method.title}</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-400 mt-0.5 leading-tight">{method.subtitle}</div>
        </div>
        <ChevronRight
          className={`w-5 h-5 text-neutral-400 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-white/10 p-3.5 sm:p-5 space-y-4 sm:space-y-5">
          {/* Platform chips */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Plataformas aceptadas</div>
            <div className="flex flex-wrap gap-2">
              {method.platforms.map((p) => (
                <span
                  key={p.name}
                  className={`text-[11px] rounded-full border px-2.5 py-1 ${p.color}`}
                >
                  {p.name} <span className="text-neutral-500 ml-1 text-[10px] normal-case">· {p.tag}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Intro / explanation */}
          <p className="text-sm text-neutral-200 leading-relaxed">{method.intro}</p>

          {/* Payment data */}
          <PaymentDataBlock variant={method.dataBlock} />

          {method.pickupChains && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04] p-3">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-1">
                <Store className="w-3 h-3" /> Cadenas donde puedes retirar
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {method.pickupChains.map((c) => (
                  <div key={c.name} className="text-xs bg-black/40 border border-white/10 rounded px-2.5 py-1.5">
                    <div className="text-emerald-200 font-semibold">{c.name}</div>
                    <div className="text-neutral-500 text-[10px]">{c.note}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tutorial */}
          <div className="rounded-xl border border-sky-500/25 bg-sky-500/[0.04] p-3.5">
            <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-2 flex items-center gap-1">
              <Info className="w-3 h-3" /> {method.tutorial.title}
            </div>
            <ol className="space-y-1.5 text-xs text-neutral-200 leading-relaxed">
              {method.tutorial.steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="shrink-0 w-4 h-4 rounded-full bg-sky-500/30 text-sky-200 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Contact CTAs */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="py-2.5 rounded-full bg-emerald-500 text-black font-semibold text-[11px] uppercase tracking-widest flex items-center justify-center gap-1 hover:bg-emerald-400"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Enviar comprobante
            </a>
            <a
              href={`tel:${PHONE_CLEAN}`}
              className="py-2.5 rounded-full border border-[#d4af37]/40 text-[#d4af37] font-semibold text-[11px] uppercase tracking-widest flex items-center justify-center gap-1 hover:bg-[#d4af37]/10"
            >
              <PhoneCall className="w-3.5 h-3.5" /> Llamar
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------
// MAIN PAGE
// --------------------------------------------------------------------------
export default function PublicCatalogPage() {
  const [openSvc, setOpenSvc] = useState(null);
  // The client first picks one of the 3 category options. `null` shows the
  // 3-choice selector; a string ("lectura" | "hechiceria" | "pacto") shows
  // just the filtered services + steps + payment methods for that category.
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { visible: visibleServices, extraCategories } = useCatalog();
  const acct = useAccountLink();

  const grouped = useMemo(() => {
    const g = { lectura: [], hechiceria: [], pacto: [] };
    for (const s of visibleServices) {
      (g[s.category] || (g[s.category] = [])).push(s);
    }
    return g;
  }, [visibleServices]);

  const categoryIds = useMemo(() => [...FIXED_CATEGORIES, ...extraCategories.map((c) => c.id)], [extraCategories]);
  const metaFor = (cat) => CATEGORY_LABEL[cat] || extraCategoryMeta(extraCategories.find((c) => c.id === cat) || { id: cat });

  const activeMeta = selectedCategory ? metaFor(selectedCategory) : null;
  const activeItems = selectedCategory ? (grouped[selectedCategory] || []) : [];

  // Whatsapp preset message for the selected category
  const catWaMsg = activeMeta
    ? encodeURIComponent(
        `Hola Crisangel 🌙\nMe interesa un(a) ${activeMeta.label}.\n\n¿Me puedes dar más información?`,
      )
    : "";

  const handleChooseCategory = (cat) => {
    setSelectedCategory(cat);
    // Scroll to top so the client sees the new category's hero
    setTimeout(() => {
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setTimeout(() => {
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#08040d] via-[#0a0510] to-black text-neutral-100" data-testid="public-catalog-page">
      {/* ---------- STICKY NAV (Home ↔ Servicios ↔ Testimonios) ---------- */}
      <nav className="sticky top-0 z-40 bg-black/70 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="catalog-home-link"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-300 hover:text-[#d4af37]"
          >
            <Home className="w-3.5 h-3.5" /> Inicio
          </Link>
          <div className="inline-flex items-center gap-1.5 text-[#d4af37] text-[10px] sm:text-[11px] uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> Servicios
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <Link
              to={acct.to}
              data-testid={acct.loggedIn ? "catalog-profile-link" : "catalog-register-link"}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-300 hover:text-[#d4af37]"
            >
              <acct.Icon className="w-3.5 h-3.5" /> {acct.short}
            </Link>
            <Link
              to="/testimonios"
              data-testid="catalog-testimonios-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-300 hover:text-[#d4af37]"
            >
              <MessageSquareQuote className="w-3.5 h-3.5" /> Testimonios
            </Link>
          </div>
        </div>
      </nav>

      {/* ---------- HERO (both states) ---------- */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#d4af37]/20 blur-3xl" />
          <div className="absolute top-20 -right-20 w-96 h-96 rounded-full bg-purple-500/15 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-5 pt-10 pb-6 sm:pt-16 sm:pb-8 text-center">
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-3">
            <div className="w-8 sm:w-10 h-px bg-[#d4af37]/40" />
            <span className="uppercase tracking-[0.35em] text-[9px] sm:text-[10px]">CrisAngel Astral</span>
            <div className="w-8 sm:w-10 h-px bg-[#d4af37]/40" />
          </div>
          {selectedCategory === null ? (
            <>
              <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white leading-tight">
                ¿Qué te interesa hoy?
              </h1>
              <p className="mt-3 sm:mt-4 text-[13px] sm:text-base text-neutral-300 max-w-2xl mx-auto leading-relaxed px-2">
                Elige una de las 3 opciones y te muestro <b>todos los servicios de esa categoría</b>,
                sus precios en pesos y dólares, los pasos para solicitarlo y todos los
                métodos de pago disponibles.
              </p>
              <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-2">
                <Link
                  to="/"
                  data-testid="catalog-hero-home-btn"
                  className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full border border-white/15 text-neutral-200 hover:text-[#d4af37] hover:border-[#d4af37]/40"
                >
                  <Home className="w-4 h-4" /> Página principal
                </Link>
                <Link
                  to={acct.to}
                  data-testid={acct.loggedIn ? "catalog-hero-profile-btn" : "catalog-hero-register-btn"}
                  className="inline-flex items-center gap-2 btn-gold text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full"
                >
                  <acct.Icon className="w-4 h-4" /> {acct.label}
                </Link>
              </div>
            </>
          ) : (
            <>
              <button
                data-testid="cat-back"
                onClick={handleReset}
                className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] uppercase tracking-widest text-neutral-400 hover:text-[#d4af37] mb-3"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Ver las 3 opciones
              </button>
              <h1 className={`font-serif text-2xl sm:text-4xl lg:text-5xl leading-tight ${activeMeta.accent}`}>
                {activeMeta.label}
              </h1>
              <p className="mt-2 sm:mt-3 text-[13px] sm:text-base text-neutral-300 max-w-2xl mx-auto leading-relaxed px-2">
                {activeMeta.desc}
              </p>
              <div className="mt-5 sm:mt-6 flex flex-wrap items-center justify-center gap-2">
                <a
                  href={`${WHATSAPP_LINK}?text=${catWaMsg}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cat-hero-wa"
                  className="inline-flex items-center gap-2 bg-emerald-500 text-black font-semibold text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full hover:bg-emerald-400"
                >
                  <MessageCircle className="w-4 h-4" /> Enviar WhatsApp
                </a>
                <Link
                  to={acct.to}
                  data-testid={acct.loggedIn ? "cat-hero-profile" : "cat-hero-register"}
                  className="inline-flex items-center gap-2 btn-gold text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full"
                >
                  <acct.Icon className="w-4 h-4" /> {acct.short}
                </Link>
              </div>
            </>
          )}
        </div>
      </header>

      {/* ---------- CATEGORY PICKER (only when nothing chosen) ---------- */}
      {selectedCategory === null && (
        <section className="max-w-5xl mx-auto px-4 sm:px-5 py-2 sm:py-6" data-testid="category-picker">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4`}>
            {categoryIds.map((cat) => {
              const meta = metaFor(cat);
              const CIcon = meta.Icon;
              const count = (grouped[cat] || []).length;
              return (
                <button
                  key={cat}
                  data-testid={`cat-choose-${cat}`}
                  onClick={() => handleChooseCategory(cat)}
                  className={`group relative text-left rounded-2xl overflow-hidden border-2 border-[#d4af37]/25 bg-black/40 hover:border-[#d4af37]/70 hover:shadow-[0_0_45px_rgba(212,175,55,0.20)] active:scale-[0.99] transition-all flex flex-col`}
                >
                  <div className={`relative h-28 sm:h-32 bg-gradient-to-br ${meta.gradient} flex items-center justify-center`}>
                    <CIcon className={`w-16 h-16 sm:w-20 sm:h-20 ${meta.accent} opacity-70 drop-shadow-lg group-hover:scale-110 transition-transform`} />
                    <div className="absolute top-2.5 right-2.5 text-[9px] uppercase tracking-widest text-white/70 bg-black/40 px-2 py-0.5 rounded-full">
                      {count} {count === 1 ? "opción" : "opciones"}
                    </div>
                  </div>
                  <div className="p-4 sm:p-5 flex-1 flex flex-col">
                    <div className={`text-[10px] uppercase tracking-widest ${meta.accent} mb-1`}>{meta.tagline}</div>
                    <h3 className="font-serif text-lg sm:text-xl text-white leading-tight mb-2">{meta.cta}</h3>
                    <ul className="space-y-1 mb-4 flex-1">
                      {meta.perks.map((p, i) => (
                        <li key={i} className="text-[12px] text-neutral-300 leading-snug flex gap-1.5">
                          <Check className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-400" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                    <div className={`mt-auto inline-flex items-center justify-between gap-2 text-[11px] uppercase tracking-widest text-[#d4af37] group-hover:translate-x-1 transition-transform`}>
                      <span>Ver servicios y precios</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-6 sm:mt-8 text-center text-[11px] sm:text-xs text-neutral-500">
            ¿Aún no sabes qué elegir?{" "}
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-emerald-300 hover:underline inline-flex items-center gap-1"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Escríbeme por WhatsApp y te asesoro
            </a>
          </div>

          {/* Trust strip — visible from the very first screen */}
          <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-4 max-w-3xl mx-auto">
            <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-3 text-center">
              <div className="font-serif text-xl sm:text-2xl gold-text leading-none">{TOTAL_COMMENTS.toLocaleString()}+</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                Clientes felices
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-3 text-center">
              <div className="font-serif text-xl sm:text-2xl gold-text leading-none">4.9★</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                Rating promedio
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/40 px-3 py-3 text-center">
              <div className="font-serif text-xl sm:text-2xl gold-text leading-none">20+</div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-neutral-400 mt-1">
                Países atendidos
              </div>
            </div>
          </div>
          <div className="mt-3 text-center">
            <Link
              to="/testimonios"
              data-testid="picker-testimonios"
              className="inline-flex items-center gap-1 text-[11px] sm:text-xs uppercase tracking-widest text-[#d4af37] hover:underline"
            >
              <MessageSquareQuote className="w-3.5 h-3.5" /> Leer testimonios reales <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </section>
      )}

      {/* ---------- SERVICES OF SELECTED CATEGORY ---------- */}
      {selectedCategory !== null && (
        <section className="max-w-6xl mx-auto px-4 sm:px-5 pt-2 sm:pt-4 pb-4 sm:pb-8">
          <div className="mb-4 sm:mb-5 flex items-center justify-between gap-2">
            <div>
              <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.35em] ${activeMeta.accent} mb-1`}>Servicios disponibles</div>
              <div className="text-white text-sm sm:text-base font-semibold">{activeItems.length} {activeItems.length === 1 ? "opción" : "opciones"} para ti</div>
            </div>
            <button
              data-testid="cat-change"
              onClick={handleReset}
              className="text-[10px] sm:text-[11px] uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 text-neutral-300 hover:text-[#d4af37] hover:border-[#d4af37]/40 inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Cambiar
            </button>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
          >
            {activeItems.map((s) => (
              <ServiceCard key={s.id} svc={s} onOpen={setOpenSvc} />
            ))}
          </motion.div>
        </section>
      )}

      {/* ---------- TESTIMONIOS TEASER (only when a category is chosen) ---------- */}
      {selectedCategory !== null && (
        <section className="max-w-4xl mx-auto px-4 sm:px-5 py-6 sm:py-10" data-testid="testimonials-teaser">
          <div className="rounded-2xl border border-[#d4af37]/30 bg-gradient-to-br from-black/60 via-purple-900/10 to-black/60 p-5 sm:p-7 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#d4af37] mb-2 flex items-center justify-center gap-1">
              <MessageSquareQuote className="w-3 h-3" /> Testimonios reales
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-white mb-3">
              +{TOTAL_COMMENTS.toLocaleString()} personas ya confiaron
            </h2>
            <div className="flex items-center justify-center gap-1 mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4af37] fill-[#d4af37]" />
              ))}
              <span className="ml-2 text-[13px] sm:text-sm text-white font-serif">4.9 / 5</span>
            </div>
            <p className="text-xs sm:text-sm text-neutral-300 max-w-xl mx-auto leading-relaxed mb-4">
              Lee historias reales de clientes en México, Estados Unidos, Canadá,
              Sudamérica y más. Filtra por país, por servicio y date cuenta de por
              qué me recomiendan hace años.
            </p>
            <Link
              to="/testimonios"
              data-testid="catalog-see-testimonials"
              className="inline-flex items-center gap-2 btn-gold text-[11px] sm:text-xs uppercase tracking-widest px-5 py-2.5 sm:py-3 rounded-full"
            >
              <MessageSquareQuote className="w-4 h-4" /> Ver todos los testimonios
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* ---------- HOW TO REQUEST (only when a category is chosen) ---------- */}
      {selectedCategory !== null && (
      <section className="max-w-4xl mx-auto px-4 sm:px-5 py-6 sm:py-8">
        <div className="rounded-2xl border border-[#d4af37]/30 bg-gradient-to-br from-[#d4af37]/[0.05] to-transparent p-4 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#d4af37] mb-3 text-center">
            <Info className="w-3 h-3 inline mr-1" /> Cómo solicitar un servicio
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-white text-center mb-5 sm:mb-6">4 pasos y listo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[...REQUEST_STEPS, {
              icon: Sparkles,
              title: selectedCategory === "lectura" ? "4. Se agenda tu cita"
                    : "4. Inicia tu trabajo espiritual",
              body: selectedCategory === "lectura"
                ? "Se agenda la cita. Para Lectura Básica desde el día siguiente; para Lectura Completa / Videollamada desde el mismo día en adelante."
                : "Se inicia el seguimiento de tu trabajo y se agenda tu lectura incluida sin costo.",
            }].map((s) => {
              const SIcon = s.icon;
              return (
                <div key={s.title} className="rounded-xl border border-white/10 bg-black/40 p-4">
                  <SIcon className="w-6 h-6 text-[#d4af37] mb-2" />
                  <div className="text-sm text-white font-semibold mb-1">{s.title}</div>
                  <div className="text-xs text-neutral-400 leading-relaxed">{s.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ---------- PAYMENT METHODS (only when a category is chosen) ---------- */}
      {selectedCategory !== null && (
      <section id="pagos" className="max-w-4xl mx-auto px-4 sm:px-5 py-8 sm:py-14 scroll-mt-8">
        <div className="text-center mb-6 sm:mb-8">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.35em] text-[#d4af37] mb-2 flex items-center justify-center gap-2">
            <div className="w-8 sm:w-10 h-px bg-[#d4af37]/40" />
            <CreditCard className="w-3.5 h-3.5" /> Métodos de pago
            <div className="w-8 sm:w-10 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-2xl sm:text-4xl text-white">Elige cómo pagar</h2>
          <p className="text-xs sm:text-sm text-neutral-400 mt-2 max-w-2xl mx-auto px-2">
            Acepto pagos desde México y desde cualquier país del mundo. Toca cada opción
            para ver los <b>datos bancarios y un tutorial paso a paso</b>.
          </p>
        </div>

        {acct.loggedIn ? (
          <div className="space-y-3">
            {PAYMENT_METHODS.map((m) => (
              <PaymentMethodCard key={m.id} method={m} />
            ))}
          </div>
        ) : (
          <div data-testid="catalog-payments-locked" className="rounded-2xl border border-[#d4af37]/30 bg-black/40 p-6 text-center">
            <Lock className="w-6 h-6 text-[#d4af37] mx-auto mb-2" />
            <h3 className="font-serif text-lg sm:text-xl text-white">Métodos de pago sólo para usuarios registrados</h3>
            <p className="text-xs sm:text-sm text-neutral-300 mt-2 max-w-xl mx-auto">Transferencia, PayPal, remesas y más. Regístrate gratis para ver los datos bancarios y los tutoriales paso a paso.</p>
            <Link to="/registro?next=/servicios" data-testid="catalog-payments-register-btn" className="mt-4 btn-gold text-[11px] sm:text-xs uppercase tracking-widest px-5 py-2.5 rounded-full inline-flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Registrarme gratis
            </Link>
          </div>
        )}

        {/* Reminder: upload receipt in profile */}
        <div className="mt-6 sm:mt-8 rounded-2xl border border-[#d4af37]/30 bg-black/40 p-4 sm:p-6 text-center">
          <acct.Icon className="w-6 h-6 text-[#d4af37] mx-auto mb-2" />
          <h3 className="font-serif text-lg sm:text-xl text-white">{acct.loggedIn ? "¿Ya pagaste? Sube tu comprobante en tu perfil" : "¿Vas a pagar? Regístrate primero"}</h3>
          <p className="text-xs sm:text-sm text-neutral-300 mt-2 max-w-xl mx-auto">
            {acct.loggedIn
              ? "En Mi perfil → Mis Pagos puedes subir tu comprobante para que lo revise personalmente, recibir tu recibo, ver tu progreso y agendar tu cita."
              : "Crea tu cuenta gratis (1 minuto) para ver precios, subir tu comprobante, recibir tu recibo y agendar tu cita."}
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Link
              to={acct.to}
              data-testid={acct.loggedIn ? "pay-profile-cta" : "pay-register-cta"}
              className="btn-gold text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full inline-flex items-center gap-2"
            >
              <acct.Icon className="w-4 h-4" /> {acct.label}
            </Link>
            <Link
              to="/"
              data-testid="pay-home-cta"
              className="text-[11px] sm:text-xs uppercase tracking-widest px-4 sm:px-5 py-2.5 sm:py-3 rounded-full border border-white/10 text-neutral-200 hover:text-[#d4af37] hover:border-[#d4af37]/40 inline-flex items-center gap-2"
            >
              <Home className="w-4 h-4" /> Página principal
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ---------- FOOTER ---------- */}
      <footer className="border-t border-white/5 py-6 sm:py-8 px-4 sm:px-5 text-center text-[11px] sm:text-xs text-neutral-500 space-y-2">
        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-emerald-300"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp {PHONE}</a>
          <span className="text-neutral-700">·</span>
          <a href={`tel:${PHONE_CLEAN}`} className="inline-flex items-center gap-1 hover:text-[#d4af37]"><Phone className="w-3.5 h-3.5" /> Llamar</a>
          <span className="text-neutral-700 hidden sm:inline">·</span>
          <a href={BANK_INFO.tiktok} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white"><Globe2 className="w-3.5 h-3.5" /> TikTok {BANK_INFO.tiktokHandle}</a>
        </div>
        <div>CrisAngel Astral</div>
        <div className="text-neutral-600">© {new Date().getFullYear()} — Todos los derechos reservados</div>
      </footer>

      <ServiceDetailModal svc={openSvc} onClose={() => setOpenSvc(null)} />
    </div>
  );
}
